import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import { Link } from 'react-router-dom';
import StandardTopSection from '../../components/standard/StandardTopSection';
import StandardListTable, {
  StandardListTableColumns
} from '../../components/tables/StandardListTable';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import StandardEditModal from './StandardEditModal';
import { API } from '../../config/api';

const API_URL = `${API.BACKEND}/api/v1/standard`;

// Parse the filter box. "3x2.5" -> {conductors:3, section:2.5};
// "5x25+16" adds the green-yellow section gy=16. Accepts comma decimals ("2,5").
// Anything without an 'x' falls back to a plain text search.
const parseFilter = raw => {
  const q = String(raw || '').trim().toLowerCase();
  if (!q) return null;

  const num = v =>
    v != null && String(v).trim() !== ''
      ? Number(String(v).trim().replace(',', '.'))
      : null;

  if (q.includes('x')) {
    const [mainPart, gyPart] = q.split('+').map(s => s.trim());
    const [cRaw, sRaw] = mainPart.split('x');
    return {
      type: 'nxs',
      conductors: num(cRaw),
      section: num(sRaw),
      gy: num(gyPart)
    };
  }

  return { type: 'text', q };
};

const matchesFilter = (row, f) => {
  if (!f) return true;

  if (f.type === 'nxs') {
    if (
      f.conductors != null &&
      !Number.isNaN(f.conductors) &&
      Number(row.numberOfConductors) !== f.conductors
    )
      return false;
    if (
      f.section != null &&
      !Number.isNaN(f.section) &&
      Number(row.section) !== f.section
    )
      return false;
    // green-yellow (a section different from the main one, on mixed cables)
    if (
      f.gy != null &&
      !Number.isNaN(f.gy) &&
      Number(row.greenYellowSection) !== f.gy
    )
      return false;
    return true;
  }

  const hay = [
    row.name,
    row.color,
    row.numberOfConductors,
    row.section,
    row.greenYellowSection,
    row.productStandard,
    row.performanceStandard
  ]
    .map(v => String(v ?? '').toLowerCase())
    .join(' ');
  return hay.includes(f.q);
};

const StandardView = () => {
  const [data, setData] = useState([]);
  const [activeCable, setActiveCable] = useState('CYYF'); // Default tab activ
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [standard, setStandard] = useState(null);
  const [counts, setCounts] = useState({ total: 0, byName: {} });
  const [filterQuery, setFilterQuery] = useState('');

  const deteleStandard = id => {
    setData(prevData => prevData.filter(item => item._id !== id));
    // keep the tab counts / total in sync after a delete
    setCounts(prev => {
      const key = String(activeCable).toUpperCase();
      const byName = { ...prev.byName };
      if (byName[key]) byName[key] -= 1;
      return { total: Math.max(0, prev.total - 1), byName };
    });
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}?name=${activeCable}`);
      setData(response.data.data); // Setăm datele în state
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchCounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/counts`);
      setCounts({
        total: response.data.total || 0,
        byName: response.data.byName || {}
      });
    } catch (error) {
      console.error('Error fetching standard counts:', error);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCable]);

  useEffect(() => {
    fetchCounts();
  }, []);

  const filteredData = useMemo(() => {
    const f = parseFilter(filterQuery);
    if (!f) return data;
    return data.filter(row => matchesFilter(row, f));
  }, [data, filterQuery]);

  const table = useAdvanceTable({
    data: filteredData,
    columns: StandardListTableColumns(
      setOpenDetailsModal,
      setStandard,
      deteleStandard
    ),
    pageSize: 6,
    pagination: true,
    sortable: true
  });

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-center">
          <h2 className="mb-0">
            <span className="me-2">Standards</span>
            <span className="fw-normal text-700">({counts.total})</span>
          </h2>
          <Link
            className="btn btn-primary px-5"
            to="/apps/standard/create-standard"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add new standard
          </Link>
        </div>
        <StandardTopSection
          activeView="list"
          activeCable={activeCable}
          setActiveCable={setActiveCable}
          counts={counts.byName}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
        />
        <StandardListTable />
        <StandardEditModal
          standard={standard}
          setData={setData}
          show={openDetailsModal}
          handleClose={() => setOpenDetailsModal(false)}
        />
      </AdvanceTableProvider>
    </div>
  );
};

export default StandardView;
